import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ReactComponent as DiamondIcon } from "../../assets/images/diamond-icon.svg";
import Button from "../../components/essentials/button/Button";
import LoadingSpinner from "../../components/essentials/loading-spin/LoadingSpinner";
import { selectUser } from "../../core/auth/authSlice";
import BillingApiRoutes from "../../shared/api-routes/billing/billing.api-routes";
import { UtilsApiRoutes } from "../../shared/api-routes/utils/utils.api-routes";
import {
	SubscriptionPlan,
	SubscriptionPlansResponse,
} from "../../shared/models";
import http from "../../shared/utils/http";
import "./SubscriptionPlans.scss";

type BillingInterval = "monthly" | "yearly";

function SubscriptionPlans() {
	const user = useSelector(selectUser);
	const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
	const [activeTab, setActiveTab] = useState<string>("premium");
	const [billingInterval, setBillingInterval] =
		useState<BillingInterval>("monthly");
	const [loading, setLoading] = useState(false);
	const [fetchingPlans, setFetchingPlans] = useState(true);

	const currentPlanId = user?.accountLevel || "free";

	const fetchPlans = async () => {
		try {
			setFetchingPlans(true);

			const response = await http.get<SubscriptionPlansResponse>(
				UtilsApiRoutes.GET_SUBSCRIPTION_PLANS,
			);

			setPlans(response.plans);

			// Set active tab to highlighted plan (premium) or first plan
			const highlightedPlan = response.plans.find((p) => p.highlighted);
			setActiveTab(highlightedPlan?.id || response.plans[0]?.id || "premium");
		} catch (err) {
			console.error("Error fetching plans:", err);
			// Fallback to mock data on error
		} finally {
			setFetchingPlans(false);
		}
	};

	useEffect(() => {
		fetchPlans();
	}, []);

	const handleSubscribe = async (planId: string) => {
		const plan = plans.find((p) => p.id === planId);
		if (!plan) return;

		if (planId === currentPlanId) {
			return; // Already on this plan
		}

		if (planId === "enterprise") {
			// Handle enterprise contact
			window.location.href =
				"mailto:info@aced-sports.com?subject=Enterprise Plan Inquiry";
			return;
		}

		setLoading(true);
		try {
			const response = await http.post(BillingApiRoutes.CHECKOUT(planId));
			if (response.url) {
				window.location.href = response.url; // redirect to Stripe Checkout
			}
		} catch (err) {
			console.error(err);
			alert("Error creating checkout session");
			setLoading(false);
		}
	};

	const getButtonText = (plan: SubscriptionPlan): string => {
		if (plan.id === currentPlanId) return "Current Plan";
		if (plan.id === "enterprise") return "Contact Us";
		return `Upgrade to ${plan.name}`;
	};

	const formatPrice = (plan: SubscriptionPlan): string => {
		const currencySymbol = plan.currency === "EUR" ? "€" : "$";
		const price =
			billingInterval === "monthly" ? plan.priceMonthly : plan.priceYearly;
		return price === 0 ? `${currencySymbol}0` : `${currencySymbol}${price}`;
	};

	const getYearlySavings = (plan: SubscriptionPlan): number => {
		const monthlyCost = plan.priceMonthly * 12;
		const yearlyCost = plan.priceYearly;
		return monthlyCost - yearlyCost;
	};

	const formatFeatureText = (feature: {
		name: string;
		limit?: string;
	}): string => {
		return feature.limit ? `${feature.name} (${feature.limit})` : feature.name;
	};

	const activePlan = plans.find((p) => p.id === activeTab);

	if (fetchingPlans) {
		return (
			<div className="subscription-plans">
				<div className="subscription-plans__container subscription-plans__loading">
					<LoadingSpinner />
				</div>
			</div>
		);
	}

	if (!activePlan || plans.length === 0) {
		return (
			<div className="subscription-plans">
				<div className="subscription-plans__container">
					<div className="subscription-plans__header">
						<h1 className="subscription-plans__title">No Plans Available</h1>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="subscription-plans">
			<div className="subscription-plans__container">
				<div className="subscription-plans__header">
					<h1 className="subscription-plans__title">Choose Your Plan</h1>
					<p className="subscription-plans__subtitle">
						Select the perfect plan for your sports career journey
					</p>
				</div>

				<div className="subscription-plans__tabs">
					{plans.map((plan) => (
						<button
							key={plan.id}
							className={`subscription-plans__tab ${
								activeTab === plan.id ? "subscription-plans__tab--active" : ""
							}`}
							onClick={() => setActiveTab(plan.id)}
						>
							{plan.highlighted && (
								<DiamondIcon className="subscription-plans__tab-icon" />
							)}
							{plan.name}
							{plan.highlighted && (
								<span className="subscription-plans__tab-badge">
									{plan.badge || "Recommended"}
								</span>
							)}
						</button>
					))}
				</div>

				<div className="subscription-plans__content">
					<div className="subscription-plans__card">
						{activeTab === currentPlanId && (
							<div className="subscription-plans__current-plan-badge">
								Current Plan
							</div>
						)}
						<div className="subscription-plans__card-header">
							<h2 className="subscription-plans__plan-name">
								{activePlan.name}
							</h2>

							{activePlan.highlighted && (
								<div className="subscription-plans__billing-toggle">
									<button
										className={`subscription-plans__billing-option ${
											billingInterval === "monthly"
												? "subscription-plans__billing-option--active"
												: ""
										}`}
										onClick={() => setBillingInterval("monthly")}
									>
										Monthly
									</button>
									<button
										className={`subscription-plans__billing-option ${
											billingInterval === "yearly"
												? "subscription-plans__billing-option--active"
												: ""
										}`}
										onClick={() => setBillingInterval("yearly")}
									>
										Annual
									</button>
								</div>
							)}

							{activePlan.id !== "enterprise" ? (
								<>
									<div className="subscription-plans__price">
										{formatPrice(activePlan)}
										<span className="subscription-plans__price-period">
											/{billingInterval === "monthly" ? "month" : "year"}
										</span>
									</div>
									{billingInterval === "yearly" &&
										getYearlySavings(activePlan) > 0 && (
											<p className="subscription-plans__savings-info">
												Save {activePlan.currency === "EUR" ? "€" : "$"}
												{getYearlySavings(activePlan)} compared to monthly
												billing
											</p>
										)}
								</>
							) : (
								<div className="subscription-plans__price subscription-plans__price--custom">
									Custom
								</div>
							)}

							<p className="subscription-plans__description">
								{activePlan.description}
							</p>
						</div>

						<div className="subscription-plans__features">
							<h3 className="subscription-plans__features-title">
								Features included:
							</h3>
							<ul className="subscription-plans__features-list">
								{activePlan.features.map((feature, index) => (
									<li
										key={index}
										className={`subscription-plans__feature ${
											!feature.included
												? "subscription-plans__feature--disabled"
												: ""
										}`}
									>
										<span className="subscription-plans__feature-icon">
											{feature.included ? "✓" : "✗"}
										</span>
										{formatFeatureText(feature)}
									</li>
								))}
							</ul>
						</div>

						{activeTab !== currentPlanId && activePlan.id !== "free" && (
							<div className="subscription-plans__card-footer">
								<Button
									className={`btn subscription-plans__button ${
										activePlan.highlighted
											? "subscription-plans__button--premium"
											: ""
									}`}
									action={() => handleSubscribe(activeTab)}
									disabled={loading}
								>
									{loading ? "Processing..." : getButtonText(activePlan)}
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default SubscriptionPlans;
